import React, { JSX } from "react";
import { Box, Divider, MenuItem, Typography } from "@mui/material";
import { useReactToPrint } from "react-to-print";
import Barcode from "react-barcode";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import mixpanelAnalytics from "../../mixpanel-analytics/mixpanelAnalytics";
import EVENT from "../../mixpanel-analytics/event";
import usePrintStyles from "../../hooks/usePrintStyles";
import { Client } from "../../interfaces/ClientInterface";
import { clientFullNameSelector } from "../../reducers/client";
import { getGenderCode } from "../Billing/PrintBill/EightyMmPrint/ClientInfo";
import { getAgeFromDOB } from "../../helpers/formatters";
import { generateLisBarcode } from "../Lab/LIS/PrintBarCodeForLIS";

export interface MedicalSampleStickerPrintInterface {
  client: Client;
  handleCloseMenu: () => void;
  isMultiplePrint?: boolean;
  labTestRecordId: number;
}

export const barCodePrintPageStyle = "@page { size: 40mm 30mm; }";
export const barCodePrintPageStyleForTwoLabels = "@page { size: 100mm 30mm; }";

export const PrintItem = ({
  label,
  value,
  isPrimaryValue = false
}: {
  label: string;
  value: string;
  isPrimaryValue?: boolean;
}): JSX.Element => {
  if (!value) return <></>;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <Typography
        component="span"
        sx={{
          fontSize: "5pt",
          fontFamily: "sans-serif",
          color: "#333"
        }}
      >
        {label}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: isPrimaryValue ? "9pt" : "7pt",
          fontFamily: "sans-serif",
          fontWeight: "bold",
          color: "#000000"
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const getAgeAndGender = (dob?: string | Date, genderCode?: string) => {
  const age = dob ? getAgeFromDOB(new Date(dob)) : null;
  const gender = genderCode ? getGenderCode(genderCode, true) : "";
  if (age && gender) {
    return `${age}Y·${gender}`;
  }
  if (genderCode) {
    return gender;
  }
  if (age) {
    return `${age}Y`;
  }
  return "";
};

const MedicalSampleStickerPrint = ({
  client,
  handleCloseMenu,
  isMultiplePrint,
  labTestRecordId
}: MedicalSampleStickerPrintInterface): JSX.Element => {
  const rc = useCurrentResourceCentre();
  const printRef = React.useRef(null);
  const print = useReactToPrint({
    content: () => printRef.current,
    onBeforePrint: () => {
      mixpanelAnalytics.track(EVENT.PRINT_MEDICAL_SAMPLE_STICKER, { rcId: rc.id, rcName: rc.name });
    }
  });

  return (
    <div>
      <MenuItem
        onClick={() => {
          print();
          handleCloseMenu();
        }}
        onMouseEnter={() => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          usePrintStyles({
            pageStyle: isMultiplePrint ? barCodePrintPageStyleForTwoLabels : barCodePrintPageStyle
          }).applyPrintStyles();
        }}
        onMouseLeave={() => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          usePrintStyles({
            pageStyle: "size: A4; margin: 0mm"
          }).applyPrintStyles();
        }}
      >
        {isMultiplePrint ? "2 Barcodes side by side" : "Single barcode"}
      </MenuItem>

      <iframe title="medical-sample-sticker-template" style={{ display: "none" }}>
        <Box ref={printRef}>
          <Box
            sx={{
              display: "flex",
              ...(isMultiplePrint
                ? { justifyContent: "space-evenly", gap: "2mm" }
                : { justifyContent: "center" })
            }}
          >
            {Array(isMultiplePrint ? 2 : 1)
              .fill(crypto.randomUUID())
              .map((item) => (
                <Box
                  key={item}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    paddingTop: 0.2
                  }}
                >
                  <PrintItem label="NAME" value={clientFullNameSelector(client)} isPrimaryValue />
                  <Divider orientation="horizontal" flexItem />
                  <Box sx={{ display: "flex", gap: 0.2 }}>
                    <PrintItem label="AGE/SEX" value={getAgeAndGender(client.dob, client.gender)} />
                    {client.passportInfo?.passportNumber && (
                      <>
                        <Divider orientation="vertical" flexItem />
                        <PrintItem label="PASSPORT NO" value={client.passportInfo.passportNumber} />
                      </>
                    )}
                  </Box>
                  <Box mt={0.2} display="flex" justifyContent="center">
                    <Barcode
                      value={generateLisBarcode(labTestRecordId)}
                      fontSize={7}
                      width={1}
                      height={18}
                      margin={0}
                    />
                  </Box>
                </Box>
              ))}
          </Box>
        </Box>
      </iframe>
    </div>
  );
};

export default MedicalSampleStickerPrint;
